import type {
  MarkdownResourceLink,
  MentionTag,
  XAdkSenderSubmitPart,
} from "@/types";

export const MARKDOWN_RESOURCE_LINK_SCHEMES = [
  "skill",
  "kb",
  // "plugin",
  // "tool",
  // "knowledge",
  // "agent",
  // "model",
] as const;

export type MarkdownResourceLinkScheme =
  | (typeof MARKDOWN_RESOURCE_LINK_SCHEMES)[number]
  | (string & {});

export interface MarkdownResourceLinkOptions {
  schemes?: readonly MarkdownResourceLinkScheme[];
}

const getResourceLinkSchemeSet = (options?: MarkdownResourceLinkOptions) =>
  new Set<string>(options?.schemes || MARKDOWN_RESOURCE_LINK_SCHEMES);

const RESOURCE_LINK_REGEX =
  /\[([^\]\n]+)\]\(([a-z][a-z0-9+.-]*:\/{2,3}[^)\s]+)\)/gi;

const stripTriggerPrefix = (label: string) => label.replace(/^[@/#$]+/, "");

export const getMarkdownResourceDisplayLabel = (label: string) =>
  stripTriggerPrefix(label).trim();

/** 将包含资源链接的 Markdown 还原成 Sender 可编辑的有序片段。 */
export const parseMarkdownResourceLinksAsParts = (
  text: string,
  options?: MarkdownResourceLinkOptions,
): XAdkSenderSubmitPart[] => {
  if (!text) return [];

  const parts: XAdkSenderSubmitPart[] = [];
  let lastIndex = 0;
  let mentionIndex = 0;
  const regex = new RegExp(
    RESOURCE_LINK_REGEX.source,
    RESOURCE_LINK_REGEX.flags,
  );

  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const source = match[0];
    const label = match[1] || "";
    const href = match[2] || "";
    const start = match.index ?? 0;
    const link = parseMarkdownResourceHref(href, label, options);
    if (!link) continue;

    if (start > lastIndex) {
      parts.push({ type: "text", text: text.slice(lastIndex, start) });
    }

    const displayName = getMarkdownResourceDisplayLabel(
      link.label || link.value,
    );
    const mention: MentionTag = {
      id: `pasted-${link.type}-${mentionIndex}-${Date.now().toString(36)}`,
      label: displayName,
      displayName,
      type: link.type,
      uri: link.href,
      targetId: link.value,
      value: link.value,
      trigger: label.match(/^[@/#$]+/)?.[0],
      data: { uri: link.href, value: link.value },
    };
    parts.push({ type: "mention", mention });
    mentionIndex += 1;
    lastIndex = start + source.length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", text: text.slice(lastIndex) });
  }
  return parts.length > 0 ? parts : [{ type: "text", text }];
};

export const parseMarkdownResourceHref = (
  href: string,
  label = "",
  options?: MarkdownResourceLinkOptions,
): MarkdownResourceLink | null => {
  const match = href.match(/^([a-z][a-z0-9+.-]*):\/{2,3}(.+)$/i);
  if (!match) return null;

  const type = match[1].toLowerCase();
  if (!getResourceLinkSchemeSet(options).has(type)) return null;

  return {
    href,
    label: label || href,
    type,
    value: decodeURIComponent(match[2]),
  };
};

export const replaceMarkdownResourceLinks = (
  text: string,
  replacer: (link: MarkdownResourceLink) => string,
  options?: MarkdownResourceLinkOptions,
) => {
  if (!text) return "";
  return text.replace(
    RESOURCE_LINK_REGEX,
    (source, label: string, href: string) => {
      const link = parseMarkdownResourceHref(href, label, options);
      return link ? replacer(link) : source;
    },
  );
};

export const formatMarkdownResourceLinksAsText = (
  text: string,
  options?: MarkdownResourceLinkOptions & { stripTrigger?: boolean },
) =>
  replaceMarkdownResourceLinks(
    text,
    (link) => {
      const label = link.label || link.value;
      return options?.stripTrigger === false
        ? label
        : stripTriggerPrefix(label);
    },
    options,
  );
